import { FCWithChildren } from '@types';
import React from 'react';

import { FaCheckCircle, FaWifi } from 'react-icons/fa';

import { Button, InputSectionGroup, Modal, ModalBody } from '@components';
import { useAppContext } from '@contexts/AppContext';
import { useToasts } from 'react-toast-notifications';
import useLayoutContext from '@hooks/useLayoutContext';

type ConfirmationModalProps = {
  closeModal: () => void;
  isOpen: boolean;
};

const ConfirmationModal: FCWithChildren<ConfirmationModalProps> = ({ isOpen, closeModal }) => {
  const { user } = useAppContext();
  const { ssid, wifiPasswordEnabled } = useLayoutContext();
  const { addToast } = useToasts();

  return (
    <Modal closeModal={closeModal} isOpen={isOpen}>
      <ModalBody>
        <div>
          <div className="text-sm font-thin">
            <div className="flex items-center">
              <FaCheckCircle className="mt-1 mr-2 text-2xl text-primary" />
              Your device has been added to the network. Please see further instructions below:
            </div>
          </div>
          <div className="p-4 my-4 rounded-md">
            {!wifiPasswordEnabled ? (
              <p>
                For your wired device, you may need to disconnect from the ethernet port, wait at
                least 1 and possibly up to 3 minutes, and then reconnect the device.
              </p>
            ) : (
              <>
                <div className="my-2">
                  <h2 className="font-thin">1. Connect Your Device to the Network: </h2>
                  {ssid && (
                    <InputSectionGroup className="mt-2">
                      <InputSectionGroup.Item>
                        <div className="input-section-group__field">
                          <label className="flex gap-4 items-center">
                            <div className="bg-blue-500 rounded-full p-2">
                              <FaWifi className="text-2xl inline" />
                            </div>
                            {ssid}
                          </label>
                        </div>
                      </InputSectionGroup.Item>
                    </InputSectionGroup>
                  )}
                </div>
                {wifiPasswordEnabled && (
                  <div className="my-2">
                    <h2 className="font-thin">2. Connect to Wi-Fi with Password:</h2>
                    <div className="flex gap-2 items-center mt-2">
                      <InputSectionGroup>
                        <InputSectionGroup.Item>
                          <div className="input-section-group__field">
                            <label className="flex gap-4 items-center">{user.preWiredKey}</label>
                          </div>
                        </InputSectionGroup.Item>
                      </InputSectionGroup>
                      <div className="flex-1">
                        <Button
                          onClick={() => {
                            navigator.clipboard.writeText(user.preWiredKey || '');
                            addToast('Wi-Fi password copied', { appearance: 'success' });
                          }}
                          size="small"
                        >
                          Copy
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="text-center">OR</div>
                <div className="font-thin">
                  If your device uses a wired connection, you may need to disconnect from the
                  ethernet port, wait at least 1 and possibly up to 3 minutes, and then reconnect
                  the device.
                </div>
              </>
            )}
          </div>
          <div>
            <Button block onClick={closeModal}>
              OK
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ConfirmationModal;
